import { NewExclusionListView } from '../view/NewExclusionListView';
import { templateNewExclusionList } from '../view/template';
import { NewExclusionListRepository } from '../repository/NewExclusionListRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { NewExclusionListWebComponent } from '..';
import { NewExclusionErrorMsg, NewExclusionErrors, NewExclusionListEvents, NewExclusionListWebComponentSchema, ExclusionItemFormEvents, ExclusionItemsResponse } from '../types';
import { getFileModules, getModulesValues, getObjectTypeModuleValues, getObjectTypeValues, GlobalExclusionsEvents, ObjectType } from '../../exclusionsPanel/types';
import { ImportedExclusionsPreviewContentWebComponent } from '../../exclusionsImportPanel';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzToastNotificationWebComponent, showSuccessToast } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { Module } from '../../exclusionItemGrid/types';
import { WEB_COMPONENT_CONFIGURATION_PROFILES } from '../../../webcomponents.constants';
import { checkExcludedItemHasValidTypeAndModules, checkExcludedItemModules, getAllAvailableModulesByObjectType, validateExcludedItem } from '../../../utils/globalExclusions/exclusionsValidationsUtils';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { translateFn } from '../../../utils/translator.helper';

export class NewExclusionListController extends BaseWebComponentController {
  public component: NewExclusionListWebComponent;
  public dataRepository: NewExclusionListRepository;
  public view: NewExclusionListView;
  public viewConstructor: typeof NewExclusionListView;
  public dataRepositoryConstructor: typeof NewExclusionListRepository;

  // exclusion types
  public EXCLUSION_TYPE_ANTIMALWARE = 1;
  public listId?: string;

  constructor(component: NewExclusionListWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: NewExclusionListView,
      viewTemplate: templateNewExclusionList,
      dataRepositoryConstructor: NewExclusionListRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$btnSave,
        event: 'click',
        callback: this.saveNewExclusionList,
      },
      {
        element: this.view.dom.$btnCancel,
        event: 'click',
        callback: this.cancelNewExclusionList,
      },
      {
        element: this.view.dom.$exclusionItemForm,
        event: ExclusionItemFormEvents.CREATE_NEW_EXCLUSION_ITEM,
        callback: this.addNewExclusionItemForm,
      },
      {
        element: this.component,
        event: NewExclusionListEvents.SET_LIST_ID,
        callback: this.setListId,
      },
      {
        element: this.view.dom.$exclusionListForm,
        event: 'importExclusions',
        callback: this.importExclusions,
      },
      {
        element: this.view.dom.$exclusionItemForm,
        event: ExclusionItemFormEvents.CHANGE_OBJECT_TYPE,
        callback: this.changeObjectType,
      },
    ];
  }

  private changeObjectType(e: WebComponentEvent) {
    const modules = getObjectTypeModuleValues(e.detail.value);
    this.view.dom.$exclusionItemForm.setAttribute('modulesOptions', JSON.stringify(modules));
  }

  private async setListId() {
    this.listId = this.component.listId;
    await this.render();
  }

  async getDataForView(): Promise<NewExclusionListWebComponentSchema | undefined> {
    const companyId = this.dependencies.getActiveCompany().companyId;
    const listId = this.component.listId;
    return await this.dataRepository.getDataForView(companyId, listId);
  }

  private async saveNewExclusionList(ev) {
    const errors: Array<any> = [];
    const actionBtn = ev.target;
    actionBtn.disabled = true;

    if (!this.component.isList) {
      try {
        const result: ExclusionItemsResponse = await this.addExclusions();
        const message = (result.data.length > 1) ? translateFn('Exclusion items created successfully.') : translateFn('Exclusion item created successfully.');
        showSuccessToast(message);
      } catch (err) {
        console.error(err);
        await this.displayErrorMessage(err);
        errors.push(err);
      }
    }

    if (this.component.isList && this.component.listId) {
      try {
        await this.updateList();
        showSuccessToast(translateFn('Exclusion list updated successfully.'));
      } catch (err) {
        console.error(err);
        await this.displayErrorMessage(err);
        errors.push(err);
      }
    }

    if (this.component.isList && !this.component.listId) {
      try {
        await this.createList();
        showSuccessToast(translateFn('Exclusion list created successfully.'));
      } catch (err) {
        console.error(err);
        await this.displayErrorMessage(err);
        errors.push(err);
      }
    }

    if (!errors.length) {
      this.dispatchSuccessEvent();
      await this.cancelNewExclusionList();
    }
    actionBtn.disabled = false;
  }

  async displayErrorMessage(err) {
    // @todo: refactor and use switch with error code
    if (err.err?.message && err.err?.message?.toLowerCase()?.indexOf(NewExclusionErrors.DUPLICATE) !== -1) {
      const { titleInput } = this.view.dom.$exclusionListForm;
      titleInput.markAsInvalid(translateFn("An exclusion list with the same name already exists."));
      return;
    }

    if (err.err?.message && err.err?.message?.toLowerCase()?.indexOf(NewExclusionErrors.INVALID) !== -1) {
      await this.createFailureNotification(translateFn("Invalid exclusion. Please update and try again."));
      return;
    }

    await this.createFailureNotification(translateFn("An error has occurred while saving. Please try again."));
  }

  private dispatchSuccessEvent() {
    const { titleInput } = this.view.dom.$exclusionListForm;
    this.component.dispatchEvent(
      new CustomEvent(GlobalExclusionsEvents.CREATE_NEW_EXCLUSION_LIST_SUCCESS, {
        detail: {
          list: {
            label: titleInput.value,
            value: this.listId,
          },
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async createList() {
    const exclusionItems = this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.dataProvider.getItems();

    const responseLists = await this.createExclusionList();
    if (!responseLists?.success) {
      throw new Error(responseLists?.data?.toString());
    }

    const responseItems = await this.createExclusionItems(exclusionItems, responseLists.data);
    if (!responseItems?.success) {
      if (responseLists?.data.length && !this.component.listId) {
        this.component.listId = responseLists.data[0];
      }
      throw new Error(responseItems?.data?.toString());
    }

    const assignItemsToList = await this.assignItemsToList(responseItems, responseLists);
    if (!assignItemsToList?.success) {
      if (responseLists?.data.length && !this.component.listId) {
        this.component.listId = responseLists.data[0];
      }
      throw new Error(assignItemsToList?.data?.toString());
    }
  }

  private async updateList() {
    const exclusionItems = this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.dataProvider.getItems();
    const responseLists: any = { success: false, data: [] };
    responseLists.data.push(this.listId);

    const responseItems = await this.createExclusionItems(exclusionItems, responseLists.data);
    if (!responseItems?.success) {
      throw new Error(responseItems?.data?.toString());
    }

    const updateExclusionList = await this.updateExclusionList();
    if (!updateExclusionList?.success) {
      throw new Error(updateExclusionList?.data?.toString());
    }

    const assignItemsToList = await this.assignItemsToList(responseItems, responseLists);
    if (!assignItemsToList?.success) {
      throw new Error(assignItemsToList?.data?.toString());
    }
  }

  private async addExclusions(): Promise<ExclusionItemsResponse> {
    const exclusionItems = this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.dataProvider.getItems();
    const responseItems = await this.createExclusionItems(exclusionItems);
    if (!responseItems?.success) {
      throw new Error(responseItems?.data?.toString());
    }
    return responseItems;
  }

  private async resetToDefault() {
    this.resetListDetails();
    this.resetExclusionItemForm();
    await this.resetExclusionItemGrid();
  }

  private async resetExclusionItemGrid() {
    this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.dataProvider.resetGrid();
    const exclusionItemGrid = this.view.dom.$exclusionItemGrid;
    const gridView = exclusionItemGrid.controller.view.dom.$gridView;
    gridView.extraParams = {
      companyId: this.dependencies.getActiveCompany().companyId,
      listId: null,
    };
    await this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.controller.handleGridItemsLoad();
  }

  private resetListDetails() {
    const { titleInput, descriptionInput } = this.view.dom.$exclusionListForm;
    titleInput.querySelector('gz-input').setFormValue('');
    this.view.dom.$exclusionListForm.formGroup.children.title.resetField();
    descriptionInput.querySelector('gz-input').setFormValue('');
  }

  private resetExclusionItemForm() {
    const { objectTypeDropDown, excludedItemInput, modulesDropDown, remarksInput } = this.view.dom.$exclusionItemForm;

    objectTypeDropDown.querySelector('gz-dropdown').setFormValue(ObjectType.FILE);
    remarksInput.querySelector('gz-input').setFormValue('');
    excludedItemInput.querySelector('gz-input').setFormValue('');
    this.view.dom.$exclusionItemForm.formGroup.children.excludedItem.resetField();
    modulesDropDown.enumList.setSelectedOptions(getFileModules());
    this.view.dom.$exclusionItemForm.setAttribute('modulesOptions', JSON.stringify(getFileModules()));
  }

  private async cancelNewExclusionList() {
    this.component.isVisible = false;
    await this.resetToDefault();
  }

  private async addNewExclusionItemForm() {
    const { objectTypeDropDown, excludedItemInput, modulesDropDown, remarksInput } = this.view.dom.$exclusionItemForm;

    const modulesCheckedOptions = modulesDropDown.enumList.getCheckedOptions();
    const modulesCheckedValue = modulesCheckedOptions.map(item => item.value);

    await this.addNewExclusionItem(
      objectTypeDropDown.value,
      excludedItemInput.value,
      modulesCheckedValue,
      remarksInput.value
    );
    await this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.controller.handleGridItemsLoad();
    this.resetExclusionItemForm();
  }

  private async addNewExclusionItem(objectType, excludedItems, modules, remarks) {
    modules.forEach((item, index) => {
      if (item === Module.ALL) {
        modules = getAllAvailableModulesByObjectType(objectType);
      }
    });

    const exclusionItem = {
      companyId: this.dependencies.getActiveCompany().companyId,
      type: this.EXCLUSION_TYPE_ANTIMALWARE,
      objectType: objectType,
      excludedItems: excludedItems,
      modules: modules,
      remarks: remarks,
    };

    this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.dataProvider.addItem(exclusionItem);
  }

  private async importExclusions(ev) {
    const file = ev.detail.file[0];
    let response;

    try {
      response = await this.importCsvAsJson(file);
    } catch (e) {
      await this.createFailureNotification('Please check the file to have the CSV format');
      return;
    }

    if (!response || !response.success) {
      await this.createFailureNotification();
      return;
    }

    const exclusions = response.data;

    const errorMessage = await this.validateExclusions(exclusions);

    if (isEmpty(errorMessage)) {
      await this.previewExclusions(exclusions, this.confirmImport.bind(this));
    } else {
      await this.createFailureNotification(errorMessage);
    }
  }

  private async importCsvAsJson(file) {
    return await this.dataRepository.importCsv('{"taskType":"int","path":"string","module":"string","remarks":"string"}', file);
  }

  public async confirmImport(exclusions) {
    for (const exclusion of exclusions) {
      await this.addNewExclusionItem(exclusion.taskType, exclusion.path, exclusion.module.split(',').map(x => parseInt(x, 10)), exclusion.remarks);
    }
    await this.view.dom.$exclusionItemGrid.controller.view.dom.$gridView.controller.handleGridItemsLoad();
    await this.createSuccessNotification();
  }

  private async createSuccessNotification(fileName = '') {
    const notificationConfig = {
      message: translateFn('CSV file successfully imported.'),
      hasAction: false,
      severity: ToastSeverity.SUCCESS,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(notificationConfig);
  }

  private async createFailureNotification(
    message = 'Please check the User guide for proper mapping of columns in the csv file for import operations'
  ) {
    const notificationConfig = {
      message: message,
      hasAction: false,
      severity: ToastSeverity.ERROR,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(notificationConfig);
  }

  private async previewExclusions(exclusions, confirmCb) {
    const configurationProfiles = document.body.querySelector(WEB_COMPONENT_CONFIGURATION_PROFILES)
      ?.parentElement as HTMLElement;
    const preview = new ImportedExclusionsPreviewContentWebComponent();

    preview.exclusions = (exclusions.slice(0, 5) || []).map(exclusion => this.formatExclusion(exclusion));
    preview.informationText = translateFn(
      'This page only displays the first five exclusions from the CSV file. The list will include all exclusions.'
    );

    const windowFooter = document.createElement('div');
    windowFooter.innerHTML = `<div><gz-button id="confirm">confirm</gz-button><gz-button id="cancel">cancel</gz-button></div>`;

    const windowConfigs: GzDialog = {
      headline: translateFn('Import CSV'),
      bodyContent: preview,
      footerContent: windowFooter,
      isOpen: true,
      isWindow: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(windowConfigs, configurationProfiles);

    windowFooter.querySelector('#confirm')?.addEventListener('click', async () => {
      confirmCb(exclusions);
      dialog.dismiss();
    });
    windowFooter.querySelector('#cancel')?.addEventListener('click', () => {
      dialog.dismiss();
    });
  }

  private formatExclusion(exclusion) {
    let modules = exclusion.module.split(',').map(x => +x);
    modules.forEach((item, index) => {
      if (item === Module.ALL) {
        modules = getAllAvailableModulesByObjectType(exclusion.taskType);
      }
    });

    const translated = getModulesValues().filter(enumValue => modules.includes(enumValue.value));
    const taskType = getObjectTypeValues().filter(enumValue => enumValue.value === exclusion.taskType);

    return {
      path: exclusion.path,
      remarks: exclusion.remarks,
      module: translated.map(module => module.label).join(', '),
      taskType: taskType[0].label,
    };
  }

  private async validateExclusions(exclusions): Promise<string> {
    let exclusion;

    for (let i = 0; i < exclusions.length; i++) {
      exclusion = exclusions[i];

      if (!exclusion || !exclusion.path || !exclusion.taskType || !exclusion.module) {
        return translateFn('Please check the file to have the CSV format');
      }

      if (
        !validateExcludedItem(exclusion.taskType, exclusion.path) ||
        !checkExcludedItemModules(exclusion.taskType, exclusion.module) ||
        !checkExcludedItemHasValidTypeAndModules(exclusion.path, exclusion.taskType, exclusion.module)
      ) {
        return translateFn(
          'One or more invalid values in exclusion rule {ruleNumber}. Check the CSV file.',
          {
            ruleNumber: i + 1,
          }
        );
      }
    }

    return '';
  }

  private async createExclusionList() {
    const { titleInput, descriptionInput } = this.view.dom.$exclusionListForm;
    const records = [
      {
        name: titleInput.value,
        description: descriptionInput.value,
        companyId: this.dependencies.getActiveCompany().companyId,
        type: this.EXCLUSION_TYPE_ANTIMALWARE,
      },
    ];

    return await this.dataRepository.createExclusionList(records);
  }

  private async updateExclusionList() {
    const { titleInput, descriptionInput } = this.view.dom.$exclusionListForm;
    const records = [{
        _id: this.listId,
        name: titleInput.value,
        description: descriptionInput.value,
        companyId: this.dependencies.getActiveCompany().companyId,
        type: this.EXCLUSION_TYPE_ANTIMALWARE,
      }];

    return await this.dataRepository.updateExclusionList(records);
  }

  private async createExclusionItems(items, listIds = []) {
    if (!items.length) {
      return { success: true, data: [] };
    }

    const records = items.map(item => {
      return {
        companyId: item.companyId,
        type: this.EXCLUSION_TYPE_ANTIMALWARE,
        specifics: {
          type: item.objectType,
          path: item.excludedItems,
          modules: item.modules,
        },
        description: item.remarks,
        listIds: listIds,
      };
    });

    const response = await this.dataRepository.createExclusionItem(records);

    if (!Array.isArray(response.data)) {
      response.data = Object.values(response.data);
    }

    return response;
  }

  private async assignItemsToList(items, list) {
    if (!items.data.length || !list.data.length) {
      return { success: true };
    }

    const companyId = this.dependencies.getActiveCompany().companyId;
    const itemsIds = items.data;
    const listId = list.data[0];

    return await this.dataRepository.assignMultipleItemsToList(companyId, itemsIds, listId);
  }
}
